import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Trophy, MapPin, Share2, Send, Sparkles, CheckCircle, Loader2, Copy, ArrowRight, MessageCircle, Pin, PinOff, Edit3, X, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { BACKEND_URL } from '@/lib/api';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import PublicSportIntakeBot from '@/components/public/PublicSportIntakeBot';
import HonorsHubSections from '@/components/public/HonorsHubSections';
import axios from 'axios';

// Phase 85 — Wall of Champions — public mobile-first roster page.
// Phase 124C — Admin curation overlay for the academy owner viewing
// their own wall: Pin to Top + Edit Milestone actions.
// Zero-auth for the public render path (intentional). Lives outside
// the AppShell, has its own layout + meta.

export default function WallOfChampions() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enquireOpen, setEnquireOpen] = useState(false);
  const [milestoneEditFor, setMilestoneEditFor] = useState(null); // student.id when modal is open
  const [savingId, setSavingId] = useState(null);

  // Phase 124C — Detect "I'm the academy owner viewing my own wall"
  const isOwnerOfWall = !!(
    user && user.role === 'academy_owner' && data?.academy?.id && user.academy_id === data.academy.id
  );

  const refreshData = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/public/academy/${slug}`);
      setData(r.data);
    } catch (_e) { /* silent — page already mounted */ }
  };

  const togglePin = async (championId, currentlyPinned) => {
    setSavingId(championId);
    try {
      await api.post(`/academy/wall-of-champions/curate/${championId}`, {
        pin_to_top: !currentlyPinned,
      });
      toast.success(currentlyPinned ? 'Unpinned from top' : 'Pinned to top');
      await refreshData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update pin');
    } finally { setSavingId(null); }
  };

  const saveMilestone = async (championId, value) => {
    setSavingId(championId);
    try {
      await api.post(`/academy/wall-of-champions/curate/${championId}`, {
        custom_milestone: value || '',
      });
      toast.success(value ? 'Milestone saved' : 'Milestone cleared');
      setMilestoneEditFor(null);
      await refreshData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save milestone');
    } finally { setSavingId(null); }
  };

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/academy/${slug}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.status === 404 ? 'not_found' : 'error'))
      .finally(() => setLoading(false));
    document.title = `Wall of Champions — Out-Play`;
  }, [slug]);

  useEffect(() => {
    if (data?.academy?.name) document.title = `${data.academy.name} — Wall of Champions`;
  }, [data]);

  const shareLink = `${window.location.origin}/academy/${slug}`;
  const copyShareLink = () => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(shareLink).then(() => toast.success('Link copied'));
    } else {
      const ta = document.createElement('textarea');
      ta.value = shareLink; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); toast.success('Link copied'); } catch {}
      document.body.removeChild(ta);
    }
  };
  const shareWhatsApp = () => {
    const text = data?.academy?.name
      ? `🏆 Check out the Wall of Champions at ${data.academy.name}! ${shareLink}`
      : `🏆 Check out the Wall of Champions! ${shareLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  // Open a WhatsApp chat directly to the academy phone — for the "Join Academy" CTA.
  // Strips spaces / parens / dashes; wa.me accepts a bare E.164 number (no '+').
  const openWhatsAppJoin = () => {
    const raw = (data?.academy?.phone || '').replace(/[^\d+]/g, '');
    if (!raw) return;
    const num = raw.startsWith('+') ? raw.slice(1) : raw;
    const greeting = `Hi! I came across ${data?.academy?.name || 'your academy'}'s Wall of Champions and would love to learn how to join. Could you share the details?`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(greeting)}`, '_blank');
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600 mx-auto" />
        <p className="text-sm text-muted-foreground mt-3">Loading champions...</p>
      </div>
    </div>
  );

  if (error === 'not_found') return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-16 relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #0a1a14 0%, #050a14 40%, #03060d 100%)',
      }}
      data-testid="wall-not-found"
    >
      {/* Ambient cinematic spotlights — gold trophy bloom + emerald field glow */}
      <div className="pointer-events-none absolute inset-0 opacity-60">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full blur-3xl"
             style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.18) 0%, transparent 65%)' }} />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[420px] h-[420px] rounded-full blur-3xl"
             style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.10) 0%, transparent 65%)' }} />
      </div>

      <div className="relative max-w-lg w-full text-center space-y-7">
        {/* Aspirational trophy mark */}
        <div className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center shadow-2xl border"
             style={{
               background: 'linear-gradient(135deg, #1a1f2e 0%, #0a0f1c 100%)',
               borderColor: 'rgba(251,191,36,0.32)',
               boxShadow: '0 18px 48px rgba(251,191,36,0.18), inset 0 1px 0 rgba(255,255,255,0.06)',
             }}>
          <Trophy className="w-9 h-9" style={{ color: '#fbbf24' }} />
        </div>

        {/* Eyebrow tag */}
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em]"
             style={{
               background: 'rgba(251,191,36,0.10)',
               border: '1px solid rgba(251,191,36,0.28)',
               color: '#fbbf24',
             }}>
          <Sparkles className="w-3 h-3" /> Wall of Champions
        </div>

        {/* Aspirational headline (Phase 195 — replaces the bare 404 panel) */}
        <h1 className="text-3xl sm:text-4xl font-display font-bold text-white tracking-tight leading-tight"
            style={{ letterSpacing: '-0.02em' }}>
          Honors Will Appear Here.
        </h1>

        {/* Supporting copy */}
        <p className="text-sm sm:text-base text-slate-400 leading-relaxed max-w-md mx-auto font-medium">
          This academy's Wall of Champions is either still being built, or the URL you followed
          doesn't match a registered academy. Champions earn their place when tournaments,
          medals, and player promotions are recorded.
        </p>

        {/* Helper actions */}
        <div className="pt-2 flex flex-col sm:flex-row gap-3 justify-center">
          <a href="https://out-play.in"
             data-testid="wall-not-found-home"
             className="inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-xl text-xs font-bold transition-all duration-300"
             style={{
               background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
               color: '#0a0a0a',
               boxShadow: '0 10px 32px rgba(251,191,36,0.32)',
             }}>
            <ArrowRight className="w-3.5 h-3.5 rotate-180" />
            Discover Out-Play
          </a>
          <a href="https://out-play.in/academies"
             data-testid="wall-not-found-browse"
             className="inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-xl text-xs font-bold transition-all duration-300"
             style={{
               background: 'rgba(255,255,255,0.04)',
               border: '1px solid rgba(255,255,255,0.12)',
               color: '#e2e8f0',
             }}>
            Browse Active Academies
          </a>
        </div>

        {/* Subtle footer signature */}
        <div className="pt-4 text-[10px] uppercase tracking-[0.22em] text-slate-600 font-bold">
          Out-Play · Athlete Honors Network
        </div>
      </div>
    </div>
  );

  if (!data) return null;
  const { academy, champions, settings } = data;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="wall-page">
      <Toaster richColors position="top-center" />

      {/* ===== HERO ===== */}
      <div className="bg-gradient-to-br from-emerald-700 via-teal-700 to-emerald-900 text-white relative overflow-hidden">
        {/* Champion photo collage backdrop — desaturated, low-opacity for depth */}
        {champions.length > 0 && (
          <div className="absolute inset-0 grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-1 opacity-[0.18] pointer-events-none" data-testid="wall-hero-collage" aria-hidden="true">
            {Array.from({ length: 24 }).map((_, i) => {
              const c = champions[i % champions.length];
              const photo = c?.photo ? `${BACKEND_URL}${c.photo}` : null;
              if (!photo) return <div key={i} className="aspect-square bg-white/10" />;
              return (
                <div key={i} className="aspect-square overflow-hidden" style={{ filter: 'grayscale(40%) saturate(0.7)' }}>
                  <img src={photo} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                </div>
              );
            })}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/40 via-emerald-800/60 to-emerald-900/85 pointer-events-none" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(white 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="relative max-w-4xl mx-auto px-5 pt-8 sm:pt-10 pb-12 sm:pb-14 text-center">
          {/* Phase 264 — Hero team photo (16:9 widescreen) above the academy name.
              Gracefully handles the missing-image case by not rendering at all. */}
          {academy.hero_image_url && (
            <div
              className="relative w-full mb-5 sm:mb-6 rounded-2xl overflow-hidden ring-1 ring-white/15 shadow-[0_18px_48px_-12px_rgba(0,0,0,0.45)]"
              data-testid="wall-hero-team-photo"
            >
              <div className="aspect-video w-full bg-emerald-950/40">
                <img
                  src={academy.hero_image_url}
                  alt={`${academy.name} team`}
                  className="w-full h-full object-cover"
                  loading="eager"
                  onError={(e) => {
                    // Gracefully hide if the image fails to load — the gradient
                    // hero behind still looks clean.
                    e.currentTarget.parentElement?.parentElement?.remove();
                  }}
                />
              </div>
              {/* Subtle gradient overlay so any overlaid text below stays legible */}
              <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-emerald-950/65 to-transparent pointer-events-none" />
            </div>
          )}

          {academy.logo_url && (
            <img src={academy.logo_url} alt={academy.name} className="h-16 w-16 mx-auto mb-3 rounded-xl bg-white p-1 object-contain shadow-xl" />
          )}
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 backdrop-blur text-[10px] uppercase tracking-wider font-semibold mb-3">
            <Trophy className="h-3 w-3" /> Wall of Champions
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold leading-tight" data-testid="wall-academy-name">
            {academy.name}
          </h1>
          {(academy.city || academy.state) && (
            <p className="text-xs sm:text-sm opacity-80 mt-1.5 inline-flex items-center gap-1.5">
              <MapPin className="h-3 w-3" /> {[academy.city, academy.state].filter(Boolean).join(', ')}
            </p>
          )}
          {academy.tagline && <p className="text-sm opacity-90 mt-2 italic">"{academy.tagline}"</p>}
          {(academy.sport_types || []).length > 0 && (
            <div className="flex flex-wrap justify-center gap-1.5 mt-3">
              {academy.sport_types.map(s => <Badge key={s} className="bg-white/20 hover:bg-white/30 border-0 text-white text-[10px]">{s}</Badge>)}
            </div>
          )}

          {/* Primary CTAs — Apply Now + WhatsApp */}
          {settings.allow_enquiries && (
            <div className="flex flex-wrap justify-center gap-2 mt-5">
              <Button size="sm" onClick={() => setEnquireOpen(true)} className="bg-amber-400 text-emerald-900 hover:bg-amber-300 font-semibold h-9 px-4 shadow-lg" data-testid="wall-apply-now-hero">
                <Send className="h-3.5 w-3.5 mr-1.5" /> Apply Now
              </Button>
              {academy.phone && (
                <Button size="sm" onClick={openWhatsAppJoin} className="bg-[#25D366] text-white hover:bg-[#1ebd5a] font-semibold h-9 px-4 shadow-lg" data-testid="wall-join-whatsapp">
                  <MessageCircle className="h-3.5 w-3.5 mr-1.5" /> Join {academy.name.length > 20 ? 'Academy' : academy.name}
                </Button>
              )}
            </div>
          )}

          {/* Secondary share row */}
          <div className="flex flex-wrap justify-center gap-2 mt-3 text-xs">
            <button onClick={shareWhatsApp} className="inline-flex items-center gap-1 text-white/80 hover:text-white" data-testid="wall-share-whatsapp">
              <Share2 className="h-3 w-3" /> Share
            </button>
            <span className="text-white/30">·</span>
            <button onClick={copyShareLink} className="inline-flex items-center gap-1 text-white/80 hover:text-white" data-testid="wall-copy-link">
              <Copy className="h-3 w-3" /> Copy link
            </button>
          </div>
        </div>
      </div>

      {/* ===== PHASE 195.c — HONORS HUB (Stat Hub + Trophy Cabinet + Progression Ladder) ===== */}
      <HonorsHubSections slug={slug} />

      {/* ===== CHAMPIONS GRID ===== */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {champions.length === 0 ? (
          <Card className="text-center py-12 max-w-md mx-auto border-dashed" data-testid="wall-empty">
            <CardContent>
              <Sparkles className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
              <h2 className="text-base font-semibold mb-1">Champions coming soon</h2>
              <p className="text-xs text-muted-foreground">Our coaches are about to publish the first quarter's progress cards.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="text-center mb-6">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">Top {champions.length} performers</p>
              <h2 className="text-lg font-display font-semibold mt-1">Ranked by overall rating · all-time best</h2>
            </div>

            {/* Phase 124C — Owner-only curation banner */}
            {isOwnerOfWall && (
              <div className="mb-5 rounded-xl border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3 flex items-center gap-3 max-w-2xl mx-auto"
                   data-testid="wall-owner-toolbar">
                <Sparkles className="h-4 w-4 text-amber-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-amber-900">Curation mode — only you see these controls</p>
                  <p className="text-[11px] text-amber-700">
                    Tap any champion's <strong className="inline-flex items-center gap-0.5"><Pin className="h-3 w-3 inline" /> Pin</strong> to lift them to the top, or <strong className="inline-flex items-center gap-0.5"><Edit3 className="h-3 w-3 inline" /> Edit</strong> to attach a milestone like "Signed to Kickstart FC".
                  </p>
                </div>
              </div>
            )}

            {/* Podium — top 3 highlighted */}
            {champions.length >= 3 && (
              <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6 max-w-2xl mx-auto" data-testid="wall-podium">
                <ChampionPodium champion={champions[1]} rank={2} accent="from-slate-300 to-slate-500"
                                isOwner={isOwnerOfWall} onPin={togglePin} onMilestone={setMilestoneEditFor} savingId={savingId} />
                <ChampionPodium champion={champions[0]} rank={1} accent="from-amber-300 to-amber-600" big
                                isOwner={isOwnerOfWall} onPin={togglePin} onMilestone={setMilestoneEditFor} savingId={savingId} />
                <ChampionPodium champion={champions[2]} rank={3} accent="from-orange-300 to-orange-600"
                                isOwner={isOwnerOfWall} onPin={togglePin} onMilestone={setMilestoneEditFor} savingId={savingId} />
              </div>
            )}

            {/* Rest */}
            <div className="grid sm:grid-cols-2 gap-3" data-testid="wall-list">
              {champions.slice(champions.length >= 3 ? 3 : 0).map((c, i) => (
                <ChampionRow key={c.id} champion={c} rank={(champions.length >= 3 ? 4 : 1) + i}
                             isOwner={isOwnerOfWall} onPin={togglePin} onMilestone={setMilestoneEditFor} savingId={savingId} />
              ))}
            </div>
          </>
        )}

        {/* ===== ENQUIRE CTA (bottom, mirrors hero for scrolled users) ===== */}
        {settings.allow_enquiries && (
          <Card className="mt-10 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/40 dark:to-teal-950/40 border-emerald-200 dark:border-emerald-900" data-testid="wall-cta">
            <CardContent className="p-5 sm:p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase font-bold text-emerald-700 tracking-wider mb-1">Could your child be next?</p>
                <h3 className="text-base sm:text-lg font-display font-semibold">Join {academy.name}</h3>
                <p className="text-xs text-muted-foreground mt-1">Send us a quick enquiry or message us on WhatsApp — no obligation.</p>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <Button size="sm" onClick={() => setEnquireOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wall-enquire-btn">
                  <Send className="h-3.5 w-3.5 mr-1.5" /> Apply Now
                </Button>
                {academy.phone && (
                  <Button size="sm" onClick={openWhatsAppJoin} className="bg-[#25D366] text-white hover:bg-[#1ebd5a]" data-testid="wall-join-whatsapp-bottom">
                    <MessageCircle className="h-3.5 w-3.5 mr-1.5" /> WhatsApp
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-[10px] text-muted-foreground mt-10">
          Powered by <a href="https://out-play.in" className="font-semibold hover:underline">Out-Play.in</a> ·
          <span className="ml-1">India's #1 Sports Academy Platform</span>
        </p>
      </div>

      <EnquireDialog open={enquireOpen} onClose={() => setEnquireOpen(false)} slug={slug} academy={academy} />

      {/* Phase 124C — Milestone edit dialog (owner-only path) */}
      {milestoneEditFor && (
        <MilestoneEditDialog
          champion={champions.find(c => c.id === milestoneEditFor)}
          onClose={() => setMilestoneEditFor(null)}
          onSave={(val) => saveMilestone(milestoneEditFor, val)}
          saving={savingId === milestoneEditFor}
        />
      )}

      {/* Phase 111 — Public AI Sports Intake Bot. Floats bottom-right; only
          mounts when the academy has set its prominent sport anchor. */}
      {settings?.intake_bot_enabled && (
        <PublicSportIntakeBot
          slug={slug}
          academyName={academy.name}
          sport={academy.prominent_sport || (academy.sport_types || [])[0] || 'sports'}
        />
      )}
    </div>
  );
}


function CurationOverlay({ champion, isOwner, onPin, onMilestone, savingId, layout = 'card' }) {
  if (!isOwner) return null;
  const isSaving = savingId === champion.id;
  // Phase 124D — Tap-first owner mode: controls ALWAYS visible (no
  // hover-gating) so mobile operators can curate from their phone
  // between batches.  Buttons sized for WCAG-compliant tap targets.
  return (
    <div
      className={`absolute z-10 ${layout === 'podium' ? 'top-1 right-1' : 'top-2 right-2'} flex gap-1.5`}
      data-testid={`wall-curation-${champion.id}`}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={() => onPin(champion.id, champion.is_pinned)}
        disabled={isSaving}
        title={champion.is_pinned ? 'Unpin from top' : 'Pin to top'}
        aria-label={champion.is_pinned ? 'Unpin from top' : 'Pin to top'}
        data-testid={`wall-pin-${champion.id}`}
        className={`h-9 w-9 sm:h-8 sm:w-8 rounded-full flex items-center justify-center transition-all shadow-md active:scale-95 ${
          champion.is_pinned
            ? 'bg-amber-500 text-white hover:bg-amber-600'
            : 'bg-white text-slate-700 hover:bg-amber-50 hover:text-amber-600 border border-slate-200'
        }`}
      >
        {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> :
          champion.is_pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
      </button>
      <button
        type="button"
        onClick={() => onMilestone(champion.id)}
        disabled={isSaving}
        title="Edit milestone badge"
        aria-label="Edit milestone badge"
        data-testid={`wall-milestone-${champion.id}`}
        className="h-9 w-9 sm:h-8 sm:w-8 rounded-full bg-white text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 border border-slate-200 flex items-center justify-center transition-all shadow-md active:scale-95"
      >
        <Edit3 className="h-4 w-4" />
      </button>
    </div>
  );
}


function ChampionPodium({ champion, rank, accent, big = false, isOwner = false, onPin, onMilestone, savingId }) {
  const photo = champion.photo ? `${BACKEND_URL}${champion.photo}` : null;
  const sizes = big ? 'h-24 w-24 sm:h-28 sm:w-28' : 'h-16 w-16 sm:h-20 sm:w-20';
  const labels = { 1: '🥇 #1', 2: '🥈 #2', 3: '🥉 #3' };
  return (
    <div className={`relative group text-center ${big ? 'order-first sm:order-2' : rank === 2 ? 'sm:order-1' : 'sm:order-3'}`}
         data-testid={`wall-champion-${rank}`}>
      <CurationOverlay champion={champion} isOwner={isOwner} onPin={onPin} onMilestone={onMilestone} savingId={savingId} layout="podium" />
      {/* Phase 124C — pinned marker (visible to everyone, not just owners) */}
      {champion.is_pinned && (
        <div className="absolute top-0 left-1 z-10" title="Pinned by academy">
          <div className="h-5 w-5 rounded-full bg-amber-500 text-white flex items-center justify-center shadow-md">
            <Pin className="h-2.5 w-2.5" />
          </div>
        </div>
      )}
      <div className={`rounded-full p-1 bg-gradient-to-br ${accent} mx-auto mb-2 shadow-lg`} style={{ width: 'fit-content' }}>
        {photo ? (
          <img src={photo} alt={champion.name} className={`${sizes} rounded-full object-cover border-2 border-white`} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        ) : (
          <div className={`${sizes} rounded-full bg-white flex items-center justify-center text-emerald-700 font-bold text-2xl`}>
            {champion.name?.[0] || '?'}
          </div>
        )}
      </div>
      <span className={`inline-block text-[10px] font-bold bg-gradient-to-r ${accent} text-white px-2 py-0.5 rounded-full mb-1`}>{labels[rank]}</span>
      <p className={`font-semibold ${big ? 'text-sm sm:text-base' : 'text-xs'} truncate px-1`} title={champion.name}>{champion.name}</p>
      <p className="text-[10px] text-muted-foreground truncate px-1">{champion.course || ''}{champion.batch ? ` · ${champion.batch}` : ''}</p>
      {/* Phase 124C — custom milestone overrides the rating, if set */}
      {champion.custom_milestone ? (
        <span className="inline-block mt-1.5 text-[10px] font-bold bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full max-w-full truncate"
              title={champion.custom_milestone}
              data-testid={`wall-milestone-badge-${champion.id}`}>
          ✨ {champion.custom_milestone}
        </span>
      ) : (
        <p className={`font-display font-bold tabular-nums text-emerald-700 mt-1 ${big ? 'text-xl sm:text-2xl' : 'text-base'}`}>{champion.rating}<span className="text-[10px] opacity-60">/5</span></p>
      )}
    </div>
  );
}


function ChampionRow({ champion, rank, isOwner = false, onPin, onMilestone, savingId }) {
  const photo = champion.photo ? `${BACKEND_URL}${champion.photo}` : null;
  const color = champion.rating >= 4 ? 'bg-emerald-500' : champion.rating >= 3 ? 'bg-blue-500' : 'bg-amber-500';
  return (
    <Card className="relative group hover:shadow-md transition-shadow" data-testid={`wall-row-${rank}`}>
      <CurationOverlay champion={champion} isOwner={isOwner} onPin={onPin} onMilestone={onMilestone} savingId={savingId} />
      <CardContent className="p-3 flex items-center gap-3">
        {champion.is_pinned && (
          <div className="absolute -top-1.5 -left-1.5" title="Pinned by academy">
            <div className="h-5 w-5 rounded-full bg-amber-500 text-white flex items-center justify-center shadow-md ring-2 ring-white">
              <Pin className="h-2.5 w-2.5" />
            </div>
          </div>
        )}
        <div className="w-7 text-center text-xs font-bold text-muted-foreground">#{rank}</div>
        {photo ? (
          <img src={photo} alt={champion.name} className="h-12 w-12 rounded-full object-cover border border-border" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        ) : (
          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 text-white flex items-center justify-center font-bold">{champion.name?.[0] || '?'}</div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">{champion.name}</p>
          <p className="text-[10px] text-muted-foreground truncate">{champion.course || ''}{champion.batch ? ` · ${champion.batch}` : ''}</p>
          {/* Phase 124C — Milestone badge replaces the rating bar if curated */}
          {champion.custom_milestone ? (
            <div className="mt-1.5">
              <span className="inline-block text-[10px] font-bold bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full max-w-full truncate"
                    title={champion.custom_milestone}
                    data-testid={`wall-milestone-badge-${champion.id}`}>
                ✨ {champion.custom_milestone}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 mt-1">
              <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div className={`h-full ${color}`} style={{ width: `${champion.rating_pct}%` }} />
              </div>
              <span className="text-xs font-bold tabular-nums">{champion.rating}/5</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


function EnquireDialog({ open, onClose, slug, academy }) {
  const [form, setForm] = useState({ parent_name: '', contact: '', student_age: '', course_interest: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.parent_name.trim() || !form.contact.trim()) {
      toast.error('Name and contact are required'); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/academy/${slug}/enquire`, {
        ...form,
        student_age: form.student_age ? parseInt(form.student_age, 10) : undefined,
      });
      setSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit. Please try again.');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setSuccess(false); setForm({ parent_name: '', contact: '', student_age: '', course_interest: '', message: '' }); } }}>
      <DialogContent className="max-w-md" data-testid="wall-enquire-dialog">
        {success ? (
          <div className="text-center py-6" data-testid="wall-enquire-success">
            <CheckCircle className="h-14 w-14 text-emerald-600 mx-auto mb-3" />
            <h3 className="text-lg font-display font-semibold mb-1">Thank you!</h3>
            <p className="text-sm text-muted-foreground">{academy?.name} will reach out shortly.</p>
            <Button size="sm" variant="outline" className="mt-5" onClick={onClose}>Close</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Enquire about {academy?.name}</DialogTitle>
              <DialogDescription className="text-xs">Drop your details — we'll get back within 24 hours.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div><Label className="text-xs">Your name *</Label>
                <Input value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} className="h-9" required data-testid="enquire-name" /></div>
              <div><Label className="text-xs">Email or phone *</Label>
                <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="h-9" placeholder="email@example.com or +91..." required data-testid="enquire-contact" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Student age</Label>
                  <Input type="number" min="3" max="30" value={form.student_age} onChange={(e) => setForm({ ...form, student_age: e.target.value })} className="h-9" data-testid="enquire-age" /></div>
                <div><Label className="text-xs">Course interest</Label>
                  <Input value={form.course_interest} onChange={(e) => setForm({ ...form, course_interest: e.target.value })} className="h-9" placeholder="e.g. Football" data-testid="enquire-course" /></div>
              </div>
              <div><Label className="text-xs">Anything else?</Label>
                <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="text-sm" rows={3} maxLength={500} data-testid="enquire-message" /></div>
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={submitting} data-testid="enquire-submit">
                {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                Send Enquiry
                <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
              <p className="text-[10px] text-center text-muted-foreground">We respect your privacy. No spam — promise.</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}


// Phase 124C — Milestone edit dialog.  Owner-only.
function MilestoneEditDialog({ champion, onClose, onSave, saving }) {
  const [value, setValue] = useState(champion?.custom_milestone || '');
  if (!champion) return null;
  const presets = [
    'Signed to Kickstart FC',
    'U-15 District Selection',
    'State Champion 2026',
    '100% NIOS Scholar',
    'Top Goalscorer · 14 goals',
    'National Camp Invitee',
  ];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="wall-milestone-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-600" /> Milestone Badge
          </DialogTitle>
          <DialogDescription className="text-xs">
            Override <strong>{champion.name}</strong>'s rating badge with a chosen achievement.
            Leave blank to revert to the algorithmic rating.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="wall-milestone-input" className="text-xs">Custom milestone</Label>
            <Input
              id="wall-milestone-input"
              value={value}
              maxLength={120}
              onChange={(e) => setValue(e.target.value)}
              placeholder="e.g., Signed to Kickstart FC"
              className="mt-1"
              data-testid="wall-milestone-input"
              autoFocus
            />
            <div className="text-[10px] text-muted-foreground mt-1 text-right">{value.length}/120</div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1.5">Quick picks</p>
            <div className="flex flex-wrap gap-1.5">
              {presets.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setValue(p)}
                  data-testid={`wall-milestone-preset-${p.replace(/\W+/g, '-').toLowerCase()}`}
                  className="text-[11px] px-2 py-1 rounded-md bg-slate-100 hover:bg-indigo-100 text-slate-700 hover:text-indigo-700 transition-colors border border-slate-200"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-between gap-2 pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSave('')}
              disabled={saving}
              className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
              data-testid="wall-milestone-clear"
            >
              <X className="h-3.5 w-3.5 mr-1" /> Clear
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
              <Button
                size="sm"
                onClick={() => onSave(value.trim())}
                disabled={saving}
                data-testid="wall-milestone-save"
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                Save
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

